/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
/* eslint-disable @atlaskit/ui-styling-standard/no-classname-prop */

/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useEffect } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { FadeIn, useExitingPersistence } from '@atlaskit/motion';
import { token } from '@atlaskit/tokens';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import type { AISmartButtonProps } from '../../common/types/shared-types';
import { useRovoButtonEventsInstrumentation } from '../../hooks/useRovoButtonEventsInstrumentation';

import { ActionShortcutIconOnly, AIActionShortcut } from './components/ai-action-shortcut';

type AIActionShortcutMenuProps = Pick<
	AISmartButtonProps,
	'product' | 'sidebarApi' | 'analyticsAttributes'
> & {
	actionShortcuts: ConversationAction[];
	onMouseEnter: () => void;
	onMouseLeave: () => void;
};

const styles = cssMap({
	actionsWrapperStyles: {
		position: 'absolute',
		// @ts-expect-error
		right: '100%',
		marginRight: token('space.100'),
		bottom: token('space.0'),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-end',
		gap: token('space.050'),
	},
	actionsWrapperIconOnlyStyles: {
		position: 'absolute',
		height: '48px',
		flexShrink: 0,
		// @ts-expect-error
		right: '100%',
		marginRight: token('space.100'),
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.100'),
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.xxlarge'),
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 8px 12px 0 rgba(30, 31, 33, 0.15)',
	},
});

export const AIActionShortcutMenu = ({
	actionShortcuts,
	product,
	sidebarApi,
	analyticsAttributes,
	onMouseEnter,
	onMouseLeave,
}: AIActionShortcutMenuProps) => {
	const { fireRovoButtonUIEvent } = useRovoButtonEventsInstrumentation();
	const { isExiting } = useExitingPersistence();

	useEffect(() => {
		fireRovoButtonUIEvent({
			source: analyticsAttributes?.productPage ?? product,
			action: 'rendered',
			actionSubject: 'rovoButton',
			actionSubjectId: 'aiRovoActionShortcutMenu',
			attributes: {
				...analyticsAttributes,
				version: 'refresh',
				actionIdsRendered: actionShortcuts.map((action) => action.id),
			},
			product,
		});
	}, [actionShortcuts, analyticsAttributes, product, fireRovoButtonUIEvent]);

	return (
		// Suspense needed here to avoid rendering empty menu when action shortcuts are loading (since they are lazy loaded)
		<React.Suspense fallback={null}>
			<FadeIn duration="large" entranceDirection="right">
				{(props) => (
					<div
						ref={props.ref}
						className={props.className}
						style={{
							...props.style,
							// Prevent hover events during exit animation to avoid flicker.
							// ExitingPersistence keeps elements mounted while exiting, so without this,
							// the exiting menu can re-trigger hover handlers and cause flickering.
							pointerEvents: isExiting ? 'none' : undefined,
						}}
						css={styles.actionsWrapperStyles}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
						onFocus={onMouseEnter}
						onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
							if (!e.currentTarget.contains(e.relatedTarget)) {
								// Only call onMouseLeave if focus is moving outside the container
								onMouseLeave();
							}
						}}
					>
						{actionShortcuts.map((action) => {
							return (
								<AIActionShortcut
									key={action.id}
									action={action}
									sidebarApi={sidebarApi}
									product={product}
									analyticsAttributes={{
										...analyticsAttributes,
										experiment: 'confluence_rovo_button_shortcuts_experiment',
										cohort: 'action-shortcut-icon-and-text',
									}}
								/>
							);
						})}
					</div>
				)}
			</FadeIn>
		</React.Suspense>
	);
};

export const AIActionShortcutMenuIconOnly = ({
	actionShortcuts,
	product,
	sidebarApi,
	analyticsAttributes,
	onMouseEnter,
	onMouseLeave,
}: AIActionShortcutMenuProps) => {
	const { fireRovoButtonUIEvent } = useRovoButtonEventsInstrumentation();
	const { isExiting } = useExitingPersistence();

	useEffect(() => {
		fireRovoButtonUIEvent({
			source: analyticsAttributes?.productPage ?? product,
			action: 'rendered',
			actionSubject: 'rovoButton',
			actionSubjectId: 'aiRovoActionShortcutMenu',
			attributes: {
				...analyticsAttributes,
				version: 'refresh',
				actionIdsRendered: actionShortcuts.map((action) => action.id),
			},
			product,
		});
	}, [actionShortcuts, analyticsAttributes, product, fireRovoButtonUIEvent]);

	return (
		// Suspense needed here to avoid rendering empty menu when action shortcuts are loading (since they are lazy loaded)
		<React.Suspense fallback={null}>
			<FadeIn duration="large" entranceDirection="right">
				{(props) => (
					<div
						ref={props.ref}
						className={props.className}
						style={{
							...props.style,
							// Prevent hover events during exit animation to avoid flicker.
							// ExitingPersistence keeps elements mounted while exiting, so without this,
							// the exiting menu can re-trigger hover handlers and cause flickering.
							pointerEvents: isExiting ? 'none' : undefined,
						}}
						css={styles.actionsWrapperIconOnlyStyles}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
						onFocus={onMouseEnter}
						onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
							if (!e.currentTarget.contains(e.relatedTarget)) {
								// Only call onMouseLeave if focus is moving outside the container
								onMouseLeave();
							}
						}}
					>
						{actionShortcuts.map((action) => {
							return (
								<ActionShortcutIconOnly
									key={action.id}
									action={action}
									sidebarApi={sidebarApi}
									product={product}
									analyticsAttributes={{
										...analyticsAttributes,
										experiment: 'confluence_rovo_button_shortcuts_experiment',
										cohort: 'action-shortcut-icon-only',
									}}
								/>
							);
						})}
					</div>
				)}
			</FadeIn>
		</React.Suspense>
	);
};
